import React, { useMemo, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { Add, ChevronLeft, ChevronRight, ChatBubble, Edit, DeleteOutline } from '@mui/icons-material';

function formatThreadTitle(title, messages) {
  if (title && String(title).trim()) return String(title);
  var firstUser = Array.isArray(messages) ? messages.find((m) => m && m.role === 'user' && m.content) : null;
  if (!firstUser) return 'Nouvelle discussion';
  var s = String(firstUser.content || '').trim();
  return s.length > 42 ? s.slice(0, 42).trim() + '…' : s;
}

export default function AssistantSidebar({
  conversations,
  activeConversationId,
  onNewConversation,
  onSelectConversation,
  onRenameConversation,
  onDeleteConversation,
  /** Appelé après sélection d’une conversation ou nouvelle discussion (ex: fermer le drawer mobile). */
  onInteraction,
}) {
  const [collapsed, setCollapsed] = useState(false);

  const { recent, older } = useMemo(() => {
    var list = Array.isArray(conversations) ? conversations : [];
    var rec = list.slice(0, 5);
    var old = list.slice(5);
    return { recent: rec, older: old };
  }, [conversations]);

  function fireInteraction() {
    if (typeof onInteraction === 'function') onInteraction();
  }

  function SidebarHeader() {
    return (
      <div className="px-3 py-3 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">
                DISCUSSIONS
              </div>
            </div>
          )}
        </div>

        <Tooltip title={collapsed ? 'Déplier la barre latérale' : 'Réduire la barre latérale'} placement="bottom" enterDelay={300}>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="shrink-0 p-2 rounded-xl border border-slate-200/90 bg-white/90 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center"
            aria-label={collapsed ? 'Déplier la sidebar' : 'Réduire la sidebar'}
          >
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </button>
        </Tooltip>
      </div>
    );
  }

  function ThreadRow({ thread }) {
    var title = formatThreadTitle(thread.title, thread.messages);
    var active = thread.id === activeConversationId;

    function handleSelect() {
      onSelectConversation(thread.id);
      fireInteraction();
    }

    const row = (
      <div
        role="button"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect();
          }
        }}
        className={`group w-full text-left px-3 py-2 rounded-2xl border transition-all duration-200 cursor-pointer hover:-translate-y-px active:scale-[0.99] ${
          active
            ? 'bg-amber-50 border-amber-300/80 shadow-sm'
            : 'bg-white/0 border-slate-200/80 hover:bg-slate-50/90 hover:shadow-sm'
        }`}
        aria-label={`Conversation ${title}`}
        aria-current={active ? 'true' : undefined}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-xl border ${
              active
                ? 'border-amber-400/50 bg-amber-100 text-amber-800'
                : 'border-slate-200 bg-slate-50 text-slate-600'
            }`}
          >
            <ChatBubble fontSize="small" />
          </span>
          {!collapsed && (
            <span className={`text-sm truncate flex-1 ${active ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>{title}</span>
          )}
          {!collapsed && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity ml-1">
              <Tooltip title="Renommer" placement="top" enterDelay={200}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!onRenameConversation) return;
                    var next = window.prompt('Renommer la conversation', title);
                    if (next != null) onRenameConversation(thread.id, next);
                  }}
                  className="w-8 h-8 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 flex items-center justify-center transition-colors min-w-[32px] min-h-[32px]"
                  aria-label="Renommer cette conversation"
                >
                  <Edit sx={{ fontSize: 16 }} />
                </button>
              </Tooltip>
              <Tooltip title="Supprimer" placement="top" enterDelay={200}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!onDeleteConversation) return;
                    var ok = window.confirm('Supprimer cette conversation ?');
                    if (ok) onDeleteConversation(thread.id);
                  }}
                  className="w-8 h-8 rounded-xl border border-slate-200 bg-white hover:bg-rose-50 text-slate-600 hover:text-rose-700 flex items-center justify-center transition-colors min-w-[32px] min-h-[32px]"
                  aria-label="Supprimer cette conversation"
                >
                  <DeleteOutline sx={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    );

    if (collapsed) {
      return (
        <Tooltip title={title} placement="right" enterDelay={350} arrow slotProps={{ popper: { sx: { zIndex: 1300 } } }}>
          {row}
        </Tooltip>
      );
    }

    return row;
  }

  function handleNewConversation() {
    onNewConversation();
    fireInteraction();
  }

  return (
    <aside
      id="assistant-discussions-sidebar"
      className={`h-full min-h-0 flex flex-col shrink-0 transition-[width] duration-300 ease-out ${
        collapsed ? 'w-[68px]' : 'w-[320px]'
      } border-r border-slate-200/90 bg-white/75 backdrop-blur-md shadow-[4px_0_24px_-12px_rgba(15,23,42,0.08)]`}
    >
      <SidebarHeader />

      <div className={`px-3 shrink-0 ${collapsed ? 'hidden' : ''}`}>
        <button
          type="button"
          onClick={handleNewConversation}
          className="w-full h-11 rounded-2xl bg-gradient-to-r from-amber-400/90 to-amber-600/90 text-slate-950 font-semibold hover:brightness-110 transition-colors flex items-center justify-center gap-2 min-h-[44px]"
        >
          <Add fontSize="small" />
          Nouvelle discussion
        </button>
      </div>

      {collapsed && (
        <div className="px-3 pb-3 shrink-0">
          <Tooltip title="Nouvelle discussion" placement="right" enterDelay={300} arrow slotProps={{ popper: { sx: { zIndex: 1300 } } }}>
            <button
              type="button"
              onClick={handleNewConversation}
              className="w-full h-11 rounded-2xl bg-gradient-to-r from-amber-400/90 to-amber-600/90 text-slate-950 font-semibold hover:brightness-110 transition-colors flex items-center justify-center min-h-[44px]"
              aria-label="Nouvelle discussion"
            >
              <Add fontSize="small" />
            </button>
          </Tooltip>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-4 scroll-smooth overscroll-contain">
        <div className={collapsed ? 'hidden' : 'px-2 pb-2 pt-1'}>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-2">Récent</div>
          <div className="space-y-2">
            {recent.map((t) => (
              <ThreadRow key={t.id} thread={t} />
            ))}
          </div>
        </div>

        {collapsed && (
          <div className="space-y-2 px-2 pt-1">
            {recent.map((t) => (
              <ThreadRow key={t.id} thread={t} />
            ))}
          </div>
        )}

        {!collapsed && older.length > 0 && (
          <div className="px-2 pt-5">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-2">Plus anciennes</div>
            <div className="space-y-2">
              {older.map((t) => (
                <ThreadRow key={t.id} thread={t} />
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
